import clientFields from "utils/settings/clientFields.json";
import clientFieldsV11_10 from "utils/settings/clientFields-v11-10.json";
import clientFieldsV11_11 from "utils/settings/clientFields-v11-11.json";
import clientFieldsV11_13 from "utils/settings/clientFields-v11-13.json";
import { unitLabels } from "utils/fields";
//********************* */
//
//  Helpers
//
//********************* */

// const getType = (type) => {
//     if (type === 'dateString') return 'string';
//     return type;
// };

const getValue = (value) =>
    typeof value === "object" && value !== null && value.constructor !== Array
        ? value.value
        : typeof value === "object" && value !== null && value.constructor === Array
        ? Boolean(value.length)
        : value;

const getId = (value) => (typeof value === "object" && value !== null ? value.id : value);

const getCodeRef = (value) => (typeof value === "object" && value !== null ? value.codeRef : value);

//********************* */
//
//  Constraints
//
//********************* */

export const required = (value) => {
    const val = getValue(value);
    // Allow 0
    return val || val === 0 ? undefined : "This field is required.";
};

export const requiredId = (value) => {
    const id = getId(value);
    // Allow 0
    return id || id === 0 ? undefined : "This field is required.";
    // return "This field is required.";
};

export const requiredCodeRef = (value) => {
    const codeRef = getCodeRef(value);
    return codeRef ? undefined : "This field is required.";
};

export const requiredIdOrCodeRef = (value) => {
    const id = getId(value);
    const codeRef = getCodeRef(value);
    return id || id === 0 || codeRef ? undefined : "This field is required.";
};

export const requiredNoZeroes = (boolVal) => {
    // trigger for tou tables to be invalid
    return boolVal == true ? undefined : "This field must not contain zeroes.";
};

export const maxLength = (max) => (value) => {
    const val = getValue(value);
    return val && val.length > max ? `Must be ${max} characters or less.` : undefined;
};

export const minValue = (min, units) => (value) => {
    const val = parseFloat(getValue(value));
    return val < min ? `Must be at least ${min}${units ? ` ${unitLabels(units)}` : ""}.` : undefined;
};

export const maxValue = (max, units) => (value) => {
    const val = parseFloat(getValue(value));
    return val > max ? `Must be less than or equal to ${max}${units ? ` ${unitLabels(units)}` : ""}.` : undefined;
};

export const decimalValidation = (decimals) => (value) => {
    const val = isNaN(value) ? 0.0 : parseFloat(getValue(value));
    var allowedDecimals = 0;

    if (decimals > 0) {
        allowedDecimals = decimals + 2;
    }

    if (val && Math.floor(val) !== val && val.toString().split(".")?.[1]?.length > allowedDecimals) {
        return decimals === 0
            ? "Number must be an integer."
            : `Number must have no more than ${decimals} decimal places.`;
    }

    return undefined;
};

//********************* */
//
//  Additional Validation
//
//********************* */

export const typeValidation = (type) => (value) => value && typeof value !== type ? `Must be a ${type}` : undefined;

// Identity / contact formats — kept separate from numeric equation fields
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
// Letters (incl. accents), spaces, hyphens, periods — no quotes, digits, or math ops
const PERSON_NAME_REGEX = /^[\p{L}\s.-]+$/u;
// Letters, numbers, and common business punctuation — no quotes or equation operators
const COMPANY_NAME_REGEX = /^[\p{L}\p{N}\s&.,\-+()/#@:]+$/u;
const QUOTE_CHARS_REGEX = /['"\u2018\u2019\u201C\u201D\u2032\u2033]/;

export const email = (value) => {
    const val = getValue(value);
    if (!val) return undefined;
    return EMAIL_REGEX.test(String(val).trim()) ? undefined : "Must be a valid email address.";
};

export const personName = (value) => {
    const val = getValue(value);
    if (!val) return undefined;
    const trimmed = String(val).trim();
    if (QUOTE_CHARS_REGEX.test(trimmed) || !PERSON_NAME_REGEX.test(trimmed)) {
        return "Must contain only letters, spaces, hyphens, or periods.";
    }
    return undefined;
};

export const companyName = (value) => {
    const val = getValue(value);
    if (!val) return undefined;
    const trimmed = String(val).trim();
    if (trimmed.startsWith("=")) {
        return "Must be a company name, not an equation.";
    }
    if (QUOTE_CHARS_REGEX.test(trimmed) || !COMPANY_NAME_REGEX.test(trimmed)) {
        return "Contains invalid characters. Letters, numbers, and common punctuation are allowed.";
    }
    return undefined;
};

const FORMAT_VALIDATORS = {
    email,
    personName,
    companyName,
};

// Field keys that should use identity format checks (via getValidation)
const FIELD_FORMATS = {
    clientFirstName: "personName",
    clientLastName: "personName",
    clientMailName: "personName",
    userNameEnteredBy: "personName",
    builderName: "companyName",
    userCompany: "companyName",
    chbaNzRenovatorFirstName: "personName",
    chbaNzRenovatorLastName: "personName",
    chbaNzRenovatorCompany: "companyName",
    chbaNzRenovatorEmail: "email",
};

//********************* */
//
//  Functions
//
//********************* */
const getFieldSettings = (fieldKey, version = "") => {
    const [h2kMajor, h2kMinor] = version.split("-");

    const targetField =
        {
            ...clientFields,
            ...(h2kMinor >= 10 ? clientFieldsV11_10 : {}),
            ...(h2kMinor >= 11 ? clientFieldsV11_11 : {}),
            ...(h2kMinor >= 13 ? clientFieldsV11_13 : {}),
        }[fieldKey] || {};

    return targetField;
};

export const getValidation = (fieldKey, version = "") => {
    const {
        constraints: { charLim = null, max = null, min = null, decimals = null, format = null } = {},
        type = null,
        baseUnits = "",
    } = getFieldSettings(fieldKey, version) || {};

    const maxLengthFunc = maxLength(charLim);
    const maxValueFunc = maxValue(max, baseUnits);
    const minValueFunc = minValue(min, baseUnits);
    const decimalValidationFunc = decimalValidation(decimals);
    const typeValidationFunc = typeValidation(type);
    const formatKey = format || FIELD_FORMATS[fieldKey];
    const formatValidationFunc = FORMAT_VALIDATORS[formatKey];

    const validation = [
        ...(charLim ? [maxLengthFunc] : []),
        ...(max || max === 0 ? [maxValueFunc] : []),
        ...(min || min === 0 ? [minValueFunc] : []),
        ...(decimals || decimals === 0 ? [decimalValidationFunc] : []),
        ...(type === "number" ? [typeValidationFunc] : []),
        ...(formatValidationFunc ? [formatValidationFunc] : []),
    ];

    return validation;
};

export const getDecimalPlaces = (fieldKey, version = "") => {
    const { constraints: { decimals = null } = {} } = getFieldSettings(fieldKey, version) || {};

    return decimals ? parseInt(decimals) : "";
};

export const validateField = (key, value, additionalRules = []) => {
    const settingsRules = getValidation(key) || [];
    // if (key === "windowNumber") {
    //     console.log("rules");
    //     settingsRules.forEach((el) => console.log(el));

    //     console.log("value", value);
    // }
    let errors = settingsRules
        .map((rule) => {
            const message = rule(value);
            return message;
        })
        .filter((error) => error);

    if (typeof additionalRules === "object")
        additionalRules.map((rule) => {
            const error = rule(value);
            errors = error ? [error, ...errors] : errors;
        });

    return {
        errors,
        invalid: errors.length > 0,
    };
};
